import { updateAppointmentFresh } from '@/lib/telehealth/updateAppointmentFresh';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { notify } from '@/lib/notify';
import type {
  Appointment,
  ChargeItemDefinition,
  Patient,
  QuestionnaireResponse,
  RelatedPerson,
} from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import {
  isResolvedClientAttendance,
  participantRsvpForAttendance,
  setAttendanceSource,
  setParticipantAttendanceStatus,
} from '@unify/fhir-model/resources/appointment';
import { MANUAL_ATTENDANCE_SOURCE_CODING } from '@/pages/session/lib/sessionAttendance';
import { resolveChargeItemDefinitionForHealthcareService } from '@unify/fhir-model/resources/charge-item-definition';
import { isCohortGroupAppointment } from '@/lib/groupTherapy';
import { resolveRelatedPersonToLinkedChartPatientIds } from '@/hooks/useMedplumPatientContacts';
import { getClientLocationLabels } from '@/lib/sessionLocation/clientLocationConfirmationFhir';
import { useAppointmentNoteSources } from '@/pages/appointments/hooks/useAppointmentNoteSources';
import { getHealthcareServiceIdFromAppointment } from '@/pages/appointments/utils/healthcareServiceNoteTemplate';
import { isMockQuestionnaireResponseId } from '@/pages/appointments/mockAppointmentNoteData';
import {
  findEncounterForAppointment,
  getPatientIdFromAppointment,
} from '@/pages/appointments/utils/fhirHelpers';
import {
  getParticipantAttendanceFromResponseItems,
  getParticipantLocationFromResponseItems,
  mergeParticipantAttendanceIntoResponseItems,
  resolveParticipantAttendanceFromAppointmentParticipant,
  type ParticipantAttendanceFhirStatus,
} from '@/pages/appointments/utils/participantAttendance';
import {
  buildPracticeLocationOptionsFromAppointment,
  getPatientLocationOptions,
} from '@/pages/appointments/utils/patientLocationOptions';
import { EPISODE_OF_CARE_CONTEXT_EXTENSION } from '@/lib/caseloadFhirConstants';
import {
  buildAppointmentParticipantRows,
  buildAppointmentServiceCodeRows,
  selectAppointmentTableAttendees,
} from './appointmentEditorTables';
import type { AppointmentParticipantRow } from '@/components/AppointmentParticipantsTable';
import type { AppointmentServiceCodeRow } from '@/components/AppointmentServiceCodesTable';

export interface UseAppointmentEditorTablesOptions {
  appointment: Appointment | undefined;
  enabled?: boolean;
  encounterId?: string;
  episodeOfCareId?: string;
  subjectPatientId?: string;
  selectedHealthcareServiceId?: string;
  questionnaireResponse?: QuestionnaireResponse | null;
  onAppointmentChange?: (appointment: Appointment) => void;
  onQuestionnaireResponseChange?: (response: QuestionnaireResponse) => void;
}

export interface UseAppointmentEditorTablesResult {
  sessionTypeName?: string;
  serviceCodeRows: AppointmentServiceCodeRow[];
  hasChargeItems: boolean;
  participantRows: AppointmentParticipantRow[];
  savedAttendanceByPatientId: Record<string, ParticipantAttendanceFhirStatus | undefined>;
  /** Merged QR + client-confirmation + local edits; used by note form sign validation. */
  locationByPatientId: Record<string, string | undefined>;
  locationOptionsByPatientId: Record<string, string[]>;
  onAttendanceChange: (participantId: string, status: ParticipantAttendanceFhirStatus) => void;
  onLocationChange: (participantId: string, location: string | undefined) => void;
  patientsById: Record<string, Patient | undefined>;
  relatedPersonsById: Record<string, RelatedPerson | undefined>;
}

export function useAppointmentEditorTables({
  appointment,
  enabled = true,
  encounterId: encounterIdProp,
  episodeOfCareId: episodeOfCareIdProp,
  subjectPatientId,
  selectedHealthcareServiceId,
  questionnaireResponse,
  onAppointmentChange,
  onQuestionnaireResponseChange,
}: UseAppointmentEditorTablesOptions): UseAppointmentEditorTablesResult {
  const medplum = useMedplum();
  const active = enabled && Boolean(appointment?.id);

  const [resolvedEncounterId, setResolvedEncounterId] = useState<string | undefined>(
    encounterIdProp,
  );
  const [attendanceByParticipantId, setAttendanceByParticipantId] = useState<
    Record<string, string>
  >({});
  const [locationByPatientId, setLocationByPatientId] = useState<
    Record<string, string | undefined>
  >({});
  const locationByPatientIdRef = useRef(locationByPatientId);
  locationByPatientIdRef.current = locationByPatientId;
  const locationPersistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [patientsById, setPatientsById] = useState<Record<string, Patient | undefined>>({});
  const [relatedPersonsById, setRelatedPersonsById] = useState<
    Record<string, RelatedPerson | undefined>
  >({});
  const [linkedPatientsByRelatedPersonId, setLinkedPatientsByRelatedPersonId] = useState<
    Record<string, Patient | undefined>
  >({});
  const [sessionTypeName, setSessionTypeName] = useState<string>();
  const [defaultBillingDefinition, setDefaultBillingDefinition] = useState<
    ChargeItemDefinition | undefined
  >();
  const [billingDefinitions, setBillingDefinitions] = useState<ChargeItemDefinition[]>([]);

  useEffect(() => {
    setResolvedEncounterId(encounterIdProp);
  }, [encounterIdProp, appointment?.id]);

  useEffect(() => {
    if (!active) {
      setBillingDefinitions([]);
      return;
    }
    let cancelled = false;
    void medplum.searchResources('ChargeItemDefinition', { _count: '200' })
      .then((definitions) => {
        if (!cancelled) setBillingDefinitions(definitions);
      })
      .catch(() => {
        if (!cancelled) setBillingDefinitions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [active, medplum]);

  useEffect(() => {
    const healthcareServiceId = selectedHealthcareServiceId
      || (appointment ? getHealthcareServiceIdFromAppointment(appointment) : undefined);
    setSessionTypeName(undefined);
    setDefaultBillingDefinition(undefined);
    if (!active || !healthcareServiceId) return;

    let cancelled = false;
    void Promise.all([
      medplum.readResource('HealthcareService', healthcareServiceId),
      resolveChargeItemDefinitionForHealthcareService(medplum, healthcareServiceId)
        .catch(() => undefined),
    ]).then(([healthcareService, definition]) => {
      if (cancelled) return;
      setSessionTypeName(healthcareService.name);
      setDefaultBillingDefinition(definition);
    }).catch(() => {
      // Legacy appointments can reference a deleted HealthcareService.
    });
    return () => {
      cancelled = true;
    };
  }, [active, appointment, medplum, selectedHealthcareServiceId]);

  useEffect(() => {
    if (!active || encounterIdProp || !appointment?.id) return;
    let cancelled = false;
    void findEncounterForAppointment(
      medplum,
      appointment.id,
      subjectPatientId ?? getPatientIdFromAppointment(appointment),
      appointment.start,
    ).then((enc) => {
      if (!cancelled) setResolvedEncounterId(enc?.id);
    });
    return () => {
      cancelled = true;
    };
  }, [active, appointment, encounterIdProp, medplum, subjectPatientId]);

  const episodeOfCareId =
    episodeOfCareIdProp
    ?? appointment?.extension
      ?.find((ex) => ex.url === EPISODE_OF_CARE_CONTEXT_EXTENSION)
      ?.valueReference?.reference?.replace('EpisodeOfCare/', '');

  const noteSources = useAppointmentNoteSources(
    active ? resolvedEncounterId : undefined,
    active ? episodeOfCareId : undefined,
  );

  const appointmentId = appointment?.id;
  const syncedForIdRef = useRef<string | null>(null);
  useEffect(() => {
    syncedForIdRef.current = null;
  }, [appointmentId]);

  useEffect(() => {
    if (!active || !appointment || appointment.id !== appointmentId) return;
    if (syncedForIdRef.current === appointmentId) return;
    syncedForIdRef.current = appointmentId ?? null;
    const statusMap: Record<string, string> = {};
    for (const p of appointment.participant ?? []) {
      const ref = p.actor?.reference?.trim();
      if (!ref?.startsWith('Patient/') && !ref?.startsWith('RelatedPerson/')) continue;
      const id = ref.slice(ref.indexOf('/') + 1).trim();
      if (!id) continue;
      statusMap[id] = resolveParticipantAttendanceFromAppointmentParticipant(p, appointment);
    }
    setAttendanceByParticipantId(statusMap);
  }, [active, appointment, appointmentId]);

  useEffect(() => {
    setLocationByPatientId({});
  }, [appointmentId]);

  // Re-seed from the response only when it is a different QuestionnaireResponse (keyed on id), so
  // in-progress location edits are not overwritten by every item change.
  const responseItemsRef = useRef(questionnaireResponse?.item);
  responseItemsRef.current = questionnaireResponse?.item;
  useEffect(() => {
    const items = responseItemsRef.current;
    if (!items) return;
    setLocationByPatientId(getParticipantLocationFromResponseItems(items));
  }, [questionnaireResponse?.id]);

  const participantClientRefsKey = useMemo(() => {
    if (!appointment) return '';
    return (appointment.participant ?? [])
      .map((p) => p.actor?.reference?.trim())
      .filter(
        (ref): ref is string =>
          Boolean(ref?.startsWith('Patient/') || ref?.startsWith('RelatedPerson/')),
      )
      .sort()
      .join('|');
  }, [appointment]);

  useEffect(() => {
    if (!active) {
      setPatientsById({});
      setRelatedPersonsById({});
      return;
    }
    const refs = participantClientRefsKey.split('|').filter(Boolean);
    if (refs.length === 0) {
      setPatientsById({});
      setRelatedPersonsById({});
      return;
    }
    const patientIds = refs
      .filter((ref) => ref.startsWith('Patient/'))
      .map((ref) => ref.slice('Patient/'.length))
      .filter(Boolean);
    const relatedPersonIds = refs
      .filter((ref) => ref.startsWith('RelatedPerson/'))
      .map((ref) => ref.slice('RelatedPerson/'.length))
      .filter(Boolean);
    let cancelled = false;
    void Promise.all([
      Promise.all(
        patientIds.map(async (id) => {
          try {
            return [id, await medplum.readResource('Patient', id)] as const;
          } catch {
            return [id, undefined] as const;
          }
        }),
      ),
      Promise.all(
        relatedPersonIds.map(async (id) => {
          try {
            return [id, await medplum.readResource('RelatedPerson', id)] as const;
          } catch {
            return [id, undefined] as const;
          }
        }),
      ),
    ]).then(([patientEntries, relatedPersonEntries]) => {
      if (cancelled) return;
      setPatientsById(Object.fromEntries(patientEntries));
      setRelatedPersonsById(Object.fromEntries(relatedPersonEntries));
    });
    return () => {
      cancelled = true;
    };
  }, [active, medplum, participantClientRefsKey]);

  const relatedPersonIdsKey = useMemo(
    () => Object.keys(relatedPersonsById).filter(Boolean).sort().join('|'),
    [relatedPersonsById],
  );

  useEffect(() => {
    if (!active) {
      setLinkedPatientsByRelatedPersonId({});
      return;
    }
    const relatedPersons = Object.values(relatedPersonsById).filter(
      (rp): rp is RelatedPerson => Boolean(rp?.id),
    );
    if (relatedPersons.length === 0) {
      setLinkedPatientsByRelatedPersonId({});
      return;
    }
    const viewingPatientId =
      subjectPatientId
      ?? (appointment ? getPatientIdFromAppointment(appointment) : undefined)
      ?? '';
    let cancelled = false;
    void resolveRelatedPersonToLinkedChartPatientIds(medplum, relatedPersons, viewingPatientId)
      .then(async (rpToPatientId) => {
        const uniquePatientIds = [...new Set(rpToPatientId.values())];
        const patientEntries = await Promise.all(
          uniquePatientIds.map(async (id) => {
            try {
              return [id, await medplum.readResource('Patient', id)] as const;
            } catch {
              return [id, undefined] as const;
            }
          }),
        );
        const patientsByLinkedId = Object.fromEntries(patientEntries);
        const linked: Record<string, Patient | undefined> = {};
        for (const [rpId, patientId] of rpToPatientId) {
          linked[rpId] = patientsByLinkedId[patientId];
        }
        return linked;
      })
      .then((linked) => {
        if (!cancelled) setLinkedPatientsByRelatedPersonId(linked);
      })
      .catch(() => {
        if (!cancelled) setLinkedPatientsByRelatedPersonId({});
      });
    return () => {
      cancelled = true;
    };
  }, [active, medplum, relatedPersonIdsKey, relatedPersonsById, subjectPatientId, appointment]);

  const serviceCodeRows = useMemo(() => {
    if (!appointment) return [];
    return buildAppointmentServiceCodeRows({
      appointment,
      encounter: noteSources.encounter,
      chargeItems: noteSources.chargeItems,
      episodeOfCare: noteSources.episodeOfCare,
      defaultBillingDefinition,
      billingDefinitions,
    });
  }, [
    appointment,
    billingDefinitions,
    defaultBillingDefinition,
    noteSources.chargeItems,
    noteSources.encounter,
    noteSources.episodeOfCare,
  ]);

  const attendees = useMemo(() => {
    if (!appointment) return [];
    return selectAppointmentTableAttendees(appointment, patientsById, relatedPersonsById);
  }, [appointment, patientsById, relatedPersonsById]);

  const locationMerged = useMemo(() => {
    if (!appointment) return {};
    return {
      ...getClientLocationLabels(noteSources.encounter, appointment),
      ...getParticipantLocationFromResponseItems(questionnaireResponse?.item),
      ...locationByPatientId,
    };
  }, [appointment, noteSources.encounter, questionnaireResponse?.item, locationByPatientId]);

  const participantRows = useMemo(() => {
    if (!appointment) return [];
    return buildAppointmentParticipantRows({
      appointment,
      attendees,
      patientsById,
      relatedPersonsById,
      linkedPatientsByRelatedPersonId,
      locationByPatientId: locationMerged,
      attendanceByParticipantId,
      subjectPatientId,
      isCohortAppointment: isCohortGroupAppointment(appointment),
    });
  }, [
    appointment,
    attendees,
    patientsById,
    relatedPersonsById,
    linkedPatientsByRelatedPersonId,
    locationMerged,
    attendanceByParticipantId,
    subjectPatientId,
  ]);

  const locationOptionsByPatientId = useMemo(() => {
    const practiceLocations = appointment
      ? buildPracticeLocationOptionsFromAppointment(appointment)
      : [];
    const fromAppt = getClientLocationLabels(noteSources.encounter, appointment);
    const out: Record<string, string[]> = {};
    for (const row of participantRows) {
      const patientForLocations = patientsById[row.id] ?? linkedPatientsByRelatedPersonId[row.id];
      const base = getPatientLocationOptions(patientForLocations, practiceLocations);
      const apptLabel = fromAppt[row.id];
      out[row.id] = apptLabel && !base.includes(apptLabel) ? [...base, apptLabel] : base;
    }
    return out;
  }, [appointment, noteSources.encounter, participantRows, patientsById, linkedPatientsByRelatedPersonId]);

  const persistLocationsToNote = useCallback(
    async (nextLocations: Record<string, string | undefined>): Promise<void> => {
      if (
        !questionnaireResponse?.id
        || !onQuestionnaireResponseChange
        || isMockQuestionnaireResponseId(questionnaireResponse.id)
      ) {
        return;
      }
      try {
        const attendanceByPatient: Record<string, ParticipantAttendanceFhirStatus | undefined> = {
          ...getParticipantAttendanceFromResponseItems(questionnaireResponse.item),
          ...(attendanceByParticipantId as Record<string, ParticipantAttendanceFhirStatus | undefined>),
        };
        const locationByPatient: Record<string, string | undefined> = {
          ...getParticipantLocationFromResponseItems(questionnaireResponse.item),
          ...nextLocations,
        };
        const rowMeta = participantRows
          .filter((row) => !row.id.startsWith('participant-'))
          .map((row) => ({ patientId: row.id, displayName: row.name }));
        const updated = {
          ...questionnaireResponse,
          item: mergeParticipantAttendanceIntoResponseItems(
            questionnaireResponse.item,
            attendanceByPatient,
            rowMeta,
            locationByPatient,
          ),
        };
        const saved = await medplum.updateResource(updated);
        onQuestionnaireResponseChange(saved);
      } catch (err) {
        notify({
          type: 'error',
          title: 'Could not save location',
          error: err,
        });
      }
    },
    [
      attendanceByParticipantId,
      medplum,
      onQuestionnaireResponseChange,
      participantRows,
      questionnaireResponse,
    ],
  );

  const onAttendanceChange = useCallback(
    (participantId: string, status: ParticipantAttendanceFhirStatus): void => {
      const appointmentId = appointment?.id;
      if (!appointmentId) return;
      setAttendanceByParticipantId((prev) => ({ ...prev, [participantId]: status }));
      void (async () => {
        try {
          // Fresh read + If-Match: bots (e.g. Chime session and transcript state) may have saved since load.
          // A resolved pick is the provider's own, so the same write stamps the source manual; an
          // unresolved pick writes nothing extra and leaves any existing source alone.
          const saved = await updateAppointmentFresh(medplum, appointmentId, (fresh) => {
            const next: Appointment = {
              ...fresh,
              participant: (fresh.participant ?? []).map((p) => {
                const ref = p.actor?.reference?.trim();
                if (ref !== `Patient/${participantId}` && ref !== `RelatedPerson/${participantId}`) {
                  return p;
                }
                const copy = { ...p };
                setParticipantAttendanceStatus(copy, status);
                // Client policies cannot read the extension: absence must also show as `declined`.
                const rsvp = participantRsvpForAttendance(status);
                if (rsvp) copy.status = rsvp;
                return copy;
              }),
            };
            if (isResolvedClientAttendance(status)) setAttendanceSource(next, MANUAL_ATTENDANCE_SOURCE_CODING);
            return next;
          });
          onAppointmentChange?.(saved);
        } catch (err) {
          notify({
            type: 'error',
            title: 'Could not save attendance',
            error: err,
          });
        }
      })();
    },
    [appointment, medplum, onAppointmentChange],
  );

  const onLocationChange = useCallback(
    (patientId: string, location: string | undefined): void => {
      setLocationByPatientId((prev) => {
        const next = { ...prev, [patientId]: location };
        locationByPatientIdRef.current = next;
        return next;
      });
      if (!questionnaireResponse?.id) return;
      if (locationPersistTimerRef.current) clearTimeout(locationPersistTimerRef.current);
      locationPersistTimerRef.current = setTimeout(() => {
        void persistLocationsToNote(locationByPatientIdRef.current);
      }, 400);
    },
    [persistLocationsToNote, questionnaireResponse?.id],
  );

  return {
    sessionTypeName,
    serviceCodeRows,
    hasChargeItems: (noteSources.chargeItems?.length ?? 0) > 0,
    participantRows,
    savedAttendanceByPatientId: attendanceByParticipantId as Record<
      string,
      ParticipantAttendanceFhirStatus | undefined
    >,
    /** Merged locations shown on the Participants table (QR + confirmation + local edits). */
    locationByPatientId: locationMerged,
    locationOptionsByPatientId,
    onAttendanceChange,
    onLocationChange,
    patientsById,
    relatedPersonsById,
  };
}
